import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '../../components/ui/sheet';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
    Search, MapPin, Star, Trophy, Award, Filter, ArrowRight,
    SlidersHorizontal, ChevronRight, AlertCircle, Sparkles, X
} from 'lucide-react';

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    visible: { transition: { staggerChildren: 0.05 } }
};

// Logo Component
function HomlyLogo({ size = 'md' }) {
    const sizes = {
        sm: 'text-xl',
        md: 'text-2xl'
    };
    
    return (
        <div className={`font-black ${sizes[size]} flex items-center gap-0.5`}>
            <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                Homly
            </span>
            <span className="text-[#FF914D]">.</span>
        </div>
    );
}

// League Badge
function LeagueBadge({ league, size = 'md' }) {
    const leagueConfig = {
        bronze: { label: 'Bronze', bg: 'from-amber-700 to-amber-500' },
        silver: { label: 'Argent', bg: 'from-gray-400 to-gray-300' },
        gold: { label: 'Or', bg: 'from-yellow-500 to-yellow-300' },
        platinum: { label: 'Platine', bg: 'from-slate-400 to-slate-200' },
        diamond: { label: 'Diamant', bg: 'from-cyan-400 to-cyan-200' }
    };
    
    const config = leagueConfig[league] || leagueConfig.bronze;
    const sizeClass = size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    
    return (
        <div className={`${sizeClass} rounded-lg bg-gradient-to-br ${config.bg} flex items-center justify-center`}>
            <Trophy className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} style={{ color: '#fff' }} />
        </div>
    );
}

// Agent Card Component
function AgentCard({ agent, onChoose }) {
    return (
        <motion.div 
            variants={fadeIn}
            whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-5 hover:border-white/20 transition-all group"
        >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                {/* Avatar */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    {agent.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                            <h3 className="font-bold text-white text-lg">{agent.full_name}</h3>
                            <p className="text-sm text-white/50">{agent.agency_name}</p>
                        </div>
                        {agent.is_provisional && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-0 text-[10px]">
                                Score provisoire
                            </Badge>
                        )}
                    </div>
                    
                    {/* Location & Zones */}
                    <div className="flex items-center gap-2 text-sm text-white/40 mb-3">
                        <MapPin className="w-3 h-3" />
                        <span>{agent.city || 'Non spécifié'}</span>
                        {agent.distance_km && (
                            <span className="text-white/30">• à {agent.distance_km} km</span>
                        )}
                    </div>
                    
                    {/* Stats row */}
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                        {/* League & Elo */}
                        <div className="flex items-center gap-2">
                            <LeagueBadge league={agent.league} />
                            <div>
                                <p className="text-sm font-bold text-white">{Math.round(agent.elo)}</p>
                                <p className="text-[10px] text-white/40">{agent.league_label}</p>
                            </div>
                        </div>
                        
                        {/* Rating */}
                        <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span className="font-bold text-white">{agent.avg_rating?.toFixed(1) || '—'}</span>
                            <span className="text-sm text-white/40">({agent.total_ratings} avis)</span>
                        </div>
                    </div>
                    
                    {/* Specialties */}
                    {agent.specialties?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                            {agent.specialties.slice(0, 3).map((spec, idx) => (
                                <Badge key={idx} variant="outline" className="border-white/10 text-white/60 text-xs">
                                    {spec}
                                </Badge>
                            ))}
                        </div>
                    )}
                    
                    {/* Badges */}
                    {agent.badges?.length > 0 && (
                        <div className="flex items-center gap-1 mb-4">
                            {agent.badges.slice(0, 4).map((badge, idx) => (
                                <div 
                                    key={idx}
                                    className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"
                                    title={badge.name}
                                >
                                    <Award className="w-4 h-4 text-[#FF914D]" />
                                </div>
                            ))}
                            {agent.badges.length > 4 && (
                                <span className="text-xs text-white/40 ml-1">+{agent.badges.length - 4}</span>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Actions */}
                <div className="flex sm:flex-col gap-2 sm:w-32">
                    <Button 
                        onClick={() => onChoose(agent)}
                        className="flex-1 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white text-sm"
                    >
                        Choisir
                    </Button>
                    <Link to={`/p/agent/${agent.id}`} className="flex-1">
                        <Button 
                            variant="outline" 
                            className="w-full border-white/10 text-white/70 hover:text-white hover:bg-white/5 text-sm"
                        >
                            Profil
                            <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                    </Link>
                </div>
            </div>
        </motion.div>
    );
}

// Lead Modal Component
function LeadModal({ agent, onClose, onSubmit }) {
    const [formData, setFormData] = useState({
        vendor_email: '',
        vendor_first_name: '',
        vendor_phone: '',
        property_city: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.vendor_email || !formData.vendor_first_name) {
            toast.error('Veuillez remplir les champs obligatoires');
            return;
        }
        
        setSubmitting(true);
        try {
            await onSubmit({
                agent_id: agent.id,
                ...formData
            });
        } finally {
            setSubmitting(false);
        }
    };
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-slate-900 rounded-2xl border border-white/10 p-6 w-full max-w-md"
            >
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-white">Contacter {agent.full_name}</h3>
                        <p className="text-sm text-white/50">{agent.agency_name}</p>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="text-sm text-white/60">Prénom *</label>
                        <Input
                            value={formData.vendor_first_name}
                            onChange={(e) => setFormData({ ...formData, vendor_first_name: e.target.value })}
                            placeholder="Jean"
                            className="bg-white/5 border-white/10 text-white"
                            required
                        />
                    </div>
                    <div>
                        <label className="text-sm text-white/60">Email *</label>
                        <Input
                            type="email"
                            value={formData.vendor_email}
                            onChange={(e) => setFormData({ ...formData, vendor_email: e.target.value })}
                            placeholder="jean@email.com"
                            className="bg-white/5 border-white/10 text-white"
                            required
                        />
                    </div>
                    <div>
                        <label className="text-sm text-white/60">Téléphone</label>
                        <Input
                            value={formData.vendor_phone}
                            onChange={(e) => setFormData({ ...formData, vendor_phone: e.target.value })}
                            placeholder="06 12 34 56 78"
                            className="bg-white/5 border-white/10 text-white"
                        />
                    </div>
                    <div>
                        <label className="text-sm text-white/60">Ville du bien</label>
                        <Input
                            value={formData.property_city}
                            onChange={(e) => setFormData({ ...formData, property_city: e.target.value })}
                            placeholder="Paris"
                            className="bg-white/5 border-white/10 text-white"
                        />
                    </div>
                    <div>
                        <label className="text-sm text-white/60">Message (optionnel)</label>
                        <textarea
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            placeholder="Décrivez brièvement votre projet..."
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 resize-none"
                            rows={3}
                        />
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                        <Button 
                            type="button"
                            variant="outline" 
                            onClick={onClose}
                            className="flex-1 border-white/10 text-white/60"
                        >
                            Annuler
                        </Button>
                        <Button 
                            type="submit"
                            disabled={submitting}
                            className="flex-1 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90"
                        >
                            {submitting ? 'Envoi...' : 'Envoyer'}
                        </Button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}

// Success Modal
function SuccessModal({ agentName, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-slate-900 rounded-2xl border border-white/10 p-8 w-full max-w-md text-center"
            >
                <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Demande envoyée !</h3>
                <p className="text-white/50 mb-6">
                    Votre demande a été transmise à {agentName}. 
                    Il vous contactera sous 24-48h.
                </p>
                <div className="space-y-2 text-left mb-6 p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-white/60 font-medium">Prochaines étapes :</p>
                    <ul className="text-sm text-white/50 space-y-1">
                        <li>1. L'agent reçoit votre demande</li>
                        <li>2. Il vous contacte pour un premier échange</li>
                        <li>3. Vous planifiez un rendez-vous</li>
                    </ul>
                </div>
                <Button 
                    onClick={onClose}
                    className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90"
                >
                    Continuer
                </Button>
            </motion.div>
        </div>
    );
}

export default function ReseauSearchPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // State
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [resolvedLocation, setResolvedLocation] = useState('');
    
    // Filters
    const [sortBy, setSortBy] = useState('recommended');
    const [leagueMin, setLeagueMin] = useState('');
    const [specialties, setSpecialties] = useState([]);
    const [availableSpecialties, setAvailableSpecialties] = useState([]);
    const [nameFilter, setNameFilter] = useState('');
    
    // Modals
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successAgentName, setSuccessAgentName] = useState('');
    
    // Autocomplete
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    
    // Load specialties
    useEffect(() => {
        loadSpecialties();
    }, []);
    
    // Search on mount
    useEffect(() => {
        searchAgents();
    }, []);
    
    const loadSpecialties = async () => {
        try {
            const response = await api.get('/reseau/filters/specialties');
            setAvailableSpecialties(response.data.specialties || []);
        } catch (error) {
            console.error('Error loading specialties:', error);
        }
    };
    
    const searchAgents = async (newPage = 1) => {
        setLoading(true);
        try {
            const response = await api.post('/reseau/search/agents', {
                query: query.trim(),
                sort_by: sortBy,
                league_min: leagueMin || null,
                specialties: specialties.length > 0 ? specialties : null,
                page: newPage,
                limit: 20
            });
            
            let results = response.data.agents || [];
            
            // Frontend filter by agent name / agency name
            if (nameFilter.trim()) {
                const f = nameFilter.toLowerCase();
                results = results.filter(a => 
                    (a.full_name || '').toLowerCase().includes(f) ||
                    (a.agency_name || '').toLowerCase().includes(f)
                );
            }
            
            setAgents(results);
            setTotal(response.data.total || 0);
            setPage(response.data.page || 1);
            setTotalPages(response.data.pages || 1);
            setResolvedLocation(response.data.search_location?.resolved || query || 'Tous');
            
            if (query) setSearchParams({ q: query });
            
            // Track search
            await api.post('/reseau/analytics/track', {
                event: 'search_performed',
                metadata: { query }
            });
        } catch (error) {
            console.error('Search error:', error);
            toast.error('Erreur lors de la recherche');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        searchAgents(1);
        setShowSuggestions(false);
    };
    
    const handleAutocomplete = useCallback(async (value) => {
        setQuery(value);
        if (value.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        
        try {
            const response = await api.get(`/reseau/search/autocomplete?q=${encodeURIComponent(value)}`);
            setSuggestions(response.data.suggestions || []);
            setShowSuggestions(true);
        } catch (error) {
            console.error('Autocomplete error:', error);
        }
    }, []);
    
    const selectSuggestion = (suggestion) => {
        setQuery(suggestion.label);
        setShowSuggestions(false);
        // Auto search
        setTimeout(() => {
            searchAgents(1);
        }, 100);
    };
    
    const handleChooseAgent = (agent) => {
        setSelectedAgent(agent);
        // Track
        api.post('/reseau/analytics/track', {
            event: 'choose_agent_click',
            agent_id: agent.id
        });
    };
    
    const handleSubmitLead = async (leadData) => {
        try {
            await api.post('/reseau/leads/create', leadData);
            setSelectedAgent(null);
            setSuccessAgentName(selectedAgent.full_name);
            setShowSuccess(true);
            
            // Track
            await api.post('/reseau/analytics/track', {
                event: 'lead_submitted',
                agent_id: leadData.agent_id
            });
        } catch (error) {
            console.error('Lead submission error:', error);
            toast.error("Erreur lors de l'envoi de la demande");
        }
    };
    
    const toggleSpecialty = (specId) => {
        if (specialties.includes(specId)) {
            setSpecialties(specialties.filter(s => s !== specId));
        } else {
            setSpecialties([...specialties, specId]);
        }
    };
    
    return (
        <div className="min-h-screen bg-background text-white">
            {/* Navbar */}
            <nav className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/reseau" className="flex items-center gap-3">
                            <HomlyLogo size="md" />
                            <span className="text-sm font-medium text-white/50">Réseau</span>
                        </Link>
                        <Link to="/login">
                            <Button variant="ghost" className="text-white/60 hover:text-white">
                                Connexion
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>
            
            {/* Search Header */}
            <div className="bg-white/[0.02] border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <form onSubmit={handleSearch} className="relative">
                        <div className="flex gap-3">
                            <div className="flex-1 relative">
                                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                <Input
                                    type="text"
                                    placeholder="Ville, code postal ou département..."
                                    value={query}
                                    onChange={(e) => handleAutocomplete(e.target.value)}
                                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                    className="w-full pl-12 pr-4 py-3 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                                />
                                
                                {/* Autocomplete dropdown */}
                                {showSuggestions && suggestions.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                                        {suggestions.map((suggestion, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => selectSuggestion(suggestion)}
                                                className="w-full px-4 py-3 text-left hover:bg-white/5 flex items-center gap-3"
                                            >
                                                <MapPin className="w-4 h-4 text-white/40" />
                                                <div>
                                                    <p className="text-white">{suggestion.label}</p>
                                                    <p className="text-xs text-white/40">{suggestion.context}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Button 
                                type="submit"
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white px-6"
                            >
                                <Search className="w-4 h-4 mr-2" />
                                Rechercher
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex gap-8">
                    {/* Filters Sidebar (Desktop) */}
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <div className="sticky top-24 space-y-6">
                            {/* Name / Agency filter */}
                            <div>
                                <h3 className="text-sm font-bold text-white mb-3">Nom agent ou agence</h3>
                                <Input
                                    type="text"
                                    placeholder="Rechercher..."
                                    value={nameFilter}
                                    onChange={(e) => { setNameFilter(e.target.value); }}
                                    onKeyUp={() => searchAgents(1)}
                                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 text-sm"
                                    data-testid="filter-name"
                                />
                            </div>
                            
                            <div>
                                <h3 className="text-sm font-bold text-white mb-3">Trier par</h3>
                                <Select value={sortBy} onValueChange={(v) => { setSortBy(v); searchAgents(1); }}>
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-white/10">
                                        <SelectItem value="recommended">Recommandés</SelectItem>
                                        <SelectItem value="rating">Meilleure note</SelectItem>
                                        <SelectItem value="reviews">Plus d'avis</SelectItem>
                                        <SelectItem value="distance">Plus proche</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <h3 className="text-sm font-bold text-white mb-3">Ligue minimum</h3>
                                <Select value={leagueMin || "all"} onValueChange={(v) => { setLeagueMin(v === "all" ? "" : v); searchAgents(1); }}>
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                        <SelectValue placeholder="Toutes" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-white/10">
                                        <SelectItem value="all">Toutes les ligues</SelectItem>
                                        <SelectItem value="bronze">Bronze+</SelectItem>
                                        <SelectItem value="silver">Argent+</SelectItem>
                                        <SelectItem value="gold">Or+</SelectItem>
                                        <SelectItem value="platinum">Platine+</SelectItem>
                                        <SelectItem value="diamond">Diamant</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <h3 className="text-sm font-bold text-white mb-3">Spécialités</h3>
                                <div className="space-y-2">
                                    {availableSpecialties.map((spec) => (
                                        <label 
                                            key={spec.id}
                                            className="flex items-center gap-2 text-sm text-white/60 cursor-pointer hover:text-white"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={specialties.includes(spec.id)}
                                                onChange={() => toggleSpecialty(spec.id)}
                                                className="rounded border-white/20 bg-white/5 text-[#681793] focus:ring-[#681793]"
                                            />
                                            {spec.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            
                            {(leagueMin || specialties.length > 0) && (
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setLeagueMin('');
                                        setSpecialties([]);
                                        searchAgents(1);
                                    }}
                                    className="w-full border-white/10 text-white/60"
                                >
                                    Réinitialiser les filtres
                                </Button>
                            )}
                        </div>
                    </div>
                    
                    {/* Results */}
                    <div className="flex-1">
                        {/* Results header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                {resolvedLocation && (
                                    <h2 className="text-lg font-bold text-white">
                                        Agents à {resolvedLocation}
                                    </h2>
                                )}
                                <p className="text-sm text-white/50">
                                    {total} agent{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}
                                </p>
                            </div>
                            
                            {/* Mobile filter button */}
                            <Sheet>
                                <SheetTrigger asChild>
                                    <Button variant="outline" className="lg:hidden border-white/10 text-white/60">
                                        <SlidersHorizontal className="w-4 h-4 mr-2" />
                                        Filtres
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="bottom" className="bg-slate-900 border-white/10">
                                    <SheetHeader>
                                        <SheetTitle className="text-white">Filtres</SheetTitle>
                                    </SheetHeader>
                                    <div className="space-y-4 py-4">
                                        {/* Mobile filters - same as sidebar */}
                                        <div>
                                            <label className="text-sm text-white/60">Trier par</label>
                                            <Select value={sortBy} onValueChange={setSortBy}>
                                                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-white/10">
                                                    <SelectItem value="recommended">Recommandés</SelectItem>
                                                    <SelectItem value="rating">Meilleure note</SelectItem>
                                                    <SelectItem value="reviews">Plus d'avis</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>
                        
                        {/* Results list */}
                        {loading ? (
                            <div className="space-y-4">
                                {[...Array(3)].map((_, i) => (
                                    <Skeleton key={i} className="h-40 w-full bg-white/5" />
                                ))}
                            </div>
                        ) : agents.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                                    <AlertCircle className="w-8 h-8 text-white/30" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">
                                    {query ? 'Aucun agent trouvé' : 'Lancez une recherche'}
                                </h3>
                                <p className="text-white/50">
                                    {query 
                                        ? 'Essayez avec une autre ville ou modifiez vos filtres.'
                                        : 'Entrez une ville ou un code postal pour trouver des agents.'}
                                </p>
                            </div>
                        ) : (
                            <motion.div 
                                initial="hidden"
                                animate="visible"
                                variants={stagger}
                                className="space-y-4"
                            >
                                {agents.map((agent) => (
                                    <AgentCard 
                                        key={agent.id} 
                                        agent={agent} 
                                        onChoose={handleChooseAgent}
                                    />
                                ))}
                            </motion.div>
                        )}
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-4 mt-8">
                                <Button
                                    variant="outline"
                                    disabled={page === 1}
                                    onClick={() => { setPage(p => p - 1); searchAgents(page - 1); }}
                                    className="border-white/10 text-white/60"
                                >
                                    Précédent
                                </Button>
                                <span className="text-sm text-white/50">
                                    Page {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    disabled={page === totalPages}
                                    onClick={() => { setPage(p => p + 1); searchAgents(page + 1); }}
                                    className="border-white/10 text-white/60"
                                >
                                    Suivant
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Modals */}
            {selectedAgent && (
                <LeadModal 
                    agent={selectedAgent}
                    onClose={() => setSelectedAgent(null)}
                    onSubmit={handleSubmitLead}
                />
            )}
            
            {showSuccess && (
                <SuccessModal 
                    agentName={successAgentName}
                    onClose={() => setShowSuccess(false)}
                />
            )}
        </div>
    );
}
